import React, { useState } from "react";
import {
    Typography,
    Box,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    InputAdornment,
    IconButton,
    Button,
    Tabs,
    Tab,
    CircularProgress
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSystemLogsQuery, useSystemLiveViewLogsQuery, SystemLogEntry } from "../../features/systems/queries";
import { useAuth } from "../../context/AuthContext";

interface SystemLogsTabProps {
    systemId: string;
    region: string;
    defaultTab?: "all" | "liveView";
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`logs-tabpanel-${index}`}
            aria-labelledby={`logs-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `logs-tab-${index}`,
        "aria-controls": `logs-tabpanel-${index}`
    };
}

const SystemLogsTab: React.FC<SystemLogsTabProps> = ({ systemId, region, defaultTab = "all" }) => {
    const { headersWithAuth } = useAuth();
    const [tabValue, setTabValue] = useState(defaultTab === "liveView" ? 1 : 0);
    const [searchText, setSearchText] = useState("");

    const {
        data: allLogsData,
        isLoading: allLogsLoading,
        isError: allLogsError,
        error: allLogsErrorData,
        refetch: refetchAllLogs
    } = useSystemLogsQuery({ id: systemId, region }, headersWithAuth);

    const {
        data: liveViewLogsData,
        isLoading: liveViewLogsLoading,
        isError: liveViewLogsError,
        error: liveViewLogsErrorData,
        refetch: refetchLiveViewLogs
    } = useSystemLiveViewLogsQuery({ id: systemId, region }, headersWithAuth);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleClearSearch = () => {
        setSearchText("");
    };

    const handleRefresh = () => {
        if (tabValue === 0) {
            refetchAllLogs();
        } else {
            refetchLiveViewLogs();
        }
    };

    const formatDateTime = (dateTimeString: string | undefined) => {
        if (!dateTimeString) return "-";

        try {
            const date = new Date(dateTimeString);
            if (isNaN(date.getTime())) {
                return dateTimeString;
            }
            return date.toLocaleString();
        } catch (e) {
            return dateTimeString;
        }
    };

    const formatLogMessage = (message: string | undefined) => {
        if (!message) return "-";

        try {
            const parsed = JSON.parse(message);
            return JSON.stringify(parsed, null, 2);
        } catch (e) {
            return message;
        }
    };

    const filterLogs = (logs: SystemLogEntry[] | undefined) => {
        if (!logs) return [];
        if (!searchText.trim()) return logs;

        const searchLower = searchText.toLowerCase().trim();
        return logs.filter(
            (log) =>
                (log.pusherMessage && log.pusherMessage.toLowerCase().includes(searchLower)) ||
                (log.creationTime && log.creationTime.toLowerCase().includes(searchLower))
        );
    };

    const renderLogTable = (logsData: any, isLoading: boolean, isError: boolean, error: any, refetch: () => void) => {
        if (isLoading) {
            return (
                <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (isError) {
            return (
                <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography color="error">
                        Error loading logs: {error instanceof Error ? error.message : "Unknown error"}
                    </Typography>
                    <Button variant="outlined" color="primary" onClick={() => refetch()} sx={{ mt: 2 }}>
                        Retry
                    </Button>
                </Box>
            );
        }

        const filteredLogs = filterLogs(logsData?.logs);

        if (!filteredLogs || filteredLogs.length === 0) {
            return (
                <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography>{searchText ? `No logs found matching "${searchText}"` : "No logs found."}</Typography>
                </Box>
            );
        }

        return (
            <>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 400 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell width="25%">Timestamp</TableCell>
                                <TableCell>Message</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredLogs.map((log: any, index: number) => (
                                <TableRow key={index} hover>
                                    <TableCell>{formatDateTime(log.creationTime)}</TableCell>
                                    <TableCell>
                                        <Typography
                                            component="pre"
                                            sx={{
                                                fontFamily: "monospace",
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                                fontSize: "0.75rem",
                                                m: 0
                                            }}
                                        >
                                            {formatLogMessage(log.pusherMessage)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={{ textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                        {searchText.trim() ? (
                            <span>
                                Showing {filteredLogs.length} results for <strong>"{searchText.trim()}"</strong>
                            </span>
                        ) : (
                            <span>Showing {filteredLogs.length} logs</span>
                        )}
                    </Typography>
                </Box>
            </>
        );
    };

    return (
        <Box sx={{ width: "100%", my: 2 }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        aria-label="log tabs"
                        textColor="primary"
                        indicatorColor="primary"
                    >
                        <Tab label="All Logs" {...a11yProps(0)} />
                        <Tab label="Live View Logs" {...a11yProps(1)} />
                    </Tabs>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <TextField
                            placeholder="Search logs..."
                            size="small"
                            variant="outlined"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: searchText && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="clear search"
                                            onClick={handleClearSearch}
                                            edge="end"
                                            size="small"
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{ width: 250 }}
                        />

                        <IconButton
                            onClick={handleRefresh}
                            color="primary"
                            size="small"
                            aria-label="refresh logs"
                            title="Refresh logs"
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Box>

            <TabPanel value={tabValue} index={0}>
                {renderLogTable(allLogsData, allLogsLoading, allLogsError, allLogsErrorData, refetchAllLogs)}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
                {renderLogTable(
                    liveViewLogsData,
                    liveViewLogsLoading,
                    liveViewLogsError,
                    liveViewLogsErrorData,
                    refetchLiveViewLogs
                )}
            </TabPanel>
        </Box>
    );
};

export default SystemLogsTab;
